// src/lib/api_heygen.ts
// Typed client for the HeyGen Video AI module (Track E9).
// Backend: apps/heygen/ — mounted at /admin/heygen with admin_auth JWT.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Upload file using FormData (no JSON content-type). */
async function djangoUpload<T = any>(path: string, formData: FormData): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail || `Upload failed: ${res.status}`, res.status);
  }
  return res.json();
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

const BASE = '/api/v1/admin/heygen';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface HeyGenAvatar {
  avatar_id: string;
  avatar_name: string;
  preview_image_url: string;
  gender?: string;
}

export interface HeyGenVoice {
  voice_id: string;
  name: string;
  language: string;
  gender: string;
  preview_audio_url?: string;
}

export type HeyGenJobType = 'video' | 'studio' | 'template' | 'translate' | 'talking_photo';
export type HeyGenJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface HeyGenJob {
  id: string;
  org_id: string;
  job_type: HeyGenJobType;
  status: HeyGenJobStatus;
  video_url: string | null;
  thumbnail_url: string | null;
  duration: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
}

export interface HeyGenTemplate {
  template_id: string;
  name: string;
  thumbnail_url: string;
  variables: Record<string, { type: string; properties?: Record<string, unknown> }>;
}

export interface HeyGenLanguage {
  language: string;
  code: string;
}

export interface Paginated<T> {
  items: T[];
  count: number;
}

// ═══════════════════════════════════════════════════════════════
// Reads (GET)
// ═══════════════════════════════════════════════════════════════

export function listAvatars(): Promise<HeyGenAvatar[]> {
  return djangoFetch(`${BASE}/avatars/`);
}

export function listVoices(): Promise<HeyGenVoice[]> {
  return djangoFetch(`${BASE}/voices/`);
}

export function listTemplates(): Promise<HeyGenTemplate[]> {
  return djangoFetch(`${BASE}/templates/`);
}

export function listLanguages(): Promise<HeyGenLanguage[]> {
  return djangoFetch(`${BASE}/languages/`);
}

export function listJobs(params?: {
  job_type?: HeyGenJobType;
  status?: HeyGenJobStatus;
  limit?: number;
  offset?: number;
}): Promise<Paginated<HeyGenJob>> {
  return djangoFetch(`${BASE}/jobs/${qs(params || {})}`);
}

// ═══════════════════════════════════════════════════════════════
// Generation (POST) — all async, return job
// ═══════════════════════════════════════════════════════════════

export interface VideoGenerateIn {
  avatar_id: string;
  voice_id: string;
  script?: string;
  audio_url?: string;
  resolution?: '720p' | '1080p' | '4K';
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  background_color?: string;
}

export function generateVideo(data: VideoGenerateIn): Promise<HeyGenJob> {
  return djangoFetch(`${BASE}/video/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface StudioSceneIn {
  avatar_id: string;
  voice_id: string;
  script?: string;
  audio_url?: string;
  background?: string;
}

export interface StudioGenerateIn {
  scenes: StudioSceneIn[];
  resolution?: '720p' | '1080p' | '4K';
  aspect_ratio?: '16:9' | '9:16' | '1:1';
}

export function generateStudio(data: StudioGenerateIn): Promise<HeyGenJob> {
  return djangoFetch(`${BASE}/studio/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface TemplateGenerateIn {
  template_id: string;
  variables: Record<string, string>;
}

export function generateFromTemplate(data: TemplateGenerateIn): Promise<HeyGenJob> {
  return djangoFetch(`${BASE}/template/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface TranslateIn {
  video_url: string;
  target_languages: string[];
  mode?: 'fast' | 'quality';
}

export function translateVideo(data: TranslateIn): Promise<HeyGenJob> {
  return djangoFetch(`${BASE}/translate/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════════
// Upload + Poll
// ═══════════════════════════════════════════════════════════════

export function uploadAudio(file: File): Promise<{ audio_url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  return djangoUpload(`${BASE}/upload/`, fd);
}

export function checkJob(jobId: string): Promise<HeyGenJob> {
  return djangoFetch(`${BASE}/jobs/${encodeURIComponent(jobId)}/check/`, {
    method: 'POST',
  });
}
